import { useState } from "react";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import { useHub } from "../../../../core/Global";
import { toastError } from "../../../../components/base/Toast";
import PaymentMethodPicker, { NEW_CARD } from "./PaymentMethodPicker";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import AddonCatalog from "../models/AddonCatalog";
import GrowthSignal, { growthFeatureOf } from "../../../../components/growth/GrowthSignal";
import "./AddonsCard.css";


const moneyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })


// Buying WhatsApp or Telegram on a plan that does not include them -- which today means Free.
//
// Deliberately not the old add-ons drawer. That one leads with a 30-day free trial and only
// asks for a payment method once the trial has been used, and there is no trial on any plan:
// Free is how somebody tries Rinvox, so what is inside it is paid for from the first day.
//
// The payment itself still goes through the existing add-on endpoints. Only the trial is
// different, and the backend takes care of that before either payment path starts.
export default function PlanAddonDrawer(props: {
    capability: AddonCatalog,
    onClose: () => void,
    onPaid: () => void
}) {

    const hub = useHub()
    const cap = props.capability

    const [methodId, methodIdSet] = useState<string>()

    // Nothing can be paid for until there is something to pay with.
    const [loadingMethods, loadingMethodsSet] = useState<boolean>(true)

    const price = moneyFormatter.format(cap.price ?? 0)

    // Written first so the payment that follows is a real charge rather than a trial, then paid
    // for the same way the old card pays: a saved card is charged inline, a new one goes through
    // Stripe Checkout and comes back to this page.
    const buy = (callback: any) => {

        hub.Post("/accounts/profile/plan/addon/prepare", { type: cap.addonType }, prep => {

            if (!prep.success) {
                toastError(prep.message ?? "Couldn't start the purchase.")
                callback(prep)
                return
            }

            if (methodId === NEW_CARD) {
                hub.Post("/accounts/profile/addon/pay/newcard", { type: cap.addonType }, res => {
                    if (res.success && res.data?.url) {
                        document.location.href = res.data.url
                        return
                    }
                    toastError(res.message ?? "Couldn't start the payment.")
                    callback(res)
                })
                return
            }

            hub.Post("/billings/payments/addons", { type: cap.addonType, paymentMethodId: methodId }, res => {
                if (res.success) props.onPaid()
                else toastError(res.message)
                callback(res)
            })
        })
    }

    return (
        <DrawerForm id="drawer-plan-addon"
            title={"Add " + cap.name}
            buttonText={"Add & Pay " + price}
            confirmDisabled={loadingMethods || !methodId}
            onClose={props.onClose}
            onSubmit={(body, callback) => buy(callback)}>

            {/* Growth signal: opened a paid add-on the plan does not include. */}
            <GrowthSignal kind="paywall" feature={growthFeatureOf(cap.key)} detail="opened to buy" />

            <div>
                <FlexRow className="mb-8">
                    {
                        /* Only the messaging add-ons have artwork. The rest wear their icon,
                           rather than a broken image where one was assumed. */
                        cap.iconType === "brands" ?
                            <img src={"/assets/addons/" + cap.key + ".png"} className="w-65px me-4" alt={cap.name} />
                            :
                            <div className="plan-addon-icon me-4">
                                <Icon icon={cap.icon} type="solid" size={28} />
                            </div>
                    }
                    <div>
                        <div className="fw-bold fs-4 mb-1">{cap.name}</div>
                        <div className="fs-7 text-gray-600">{price} / month</div>
                    </div>
                </FlexRow>

                <p className="fs-6 mb-8">
                    Your plan does not include {cap.name}. Adding it starts a monthly subscription
                    straight away and charges the payment method you choose below. It renews every
                    month until you cancel, and you can cancel it whenever you like.
                </p>

                <div className="fw-bold mb-3">Payment Method</div>
                <PaymentMethodPicker selectedMethodId={methodId} onChange={methodIdSet}
                    onLoadingChange={loadingMethodsSet} />
            </div>

        </DrawerForm>
    )
}
